import { motion } from 'framer-motion';
import { Button } from '../../ui/Button';
import { Card, CardContent } from '../../ui/Card';
import { MoonIcon, SparklesIcon } from '@heroicons/react/24/outline';

interface NightlyWelcomeProps {
  onBegin: () => void;
}

export function NightlyWelcome({ onBegin }: NightlyWelcomeProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] as const }}
      className="min-h-screen bg-surface-50 dark:bg-surface-900 px-4 py-8 flex items-center justify-center"
    >
      <div className="max-w-2xl w-full space-y-6">
        {/* Header */}
        <div className="text-center space-y-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary-100 dark:bg-primary-900/30 mb-4"
          >
            <MoonIcon className="w-10 h-10 text-primary-600 dark:text-primary-400" />
          </motion.div>

          <h1 className="text-4xl font-bold text-surface-900 dark:text-surface-100">
            Nightly Review
          </h1>
          <p className="text-lg text-surface-600 dark:text-surface-400">
            Take a few moments to reflect on your day
          </p>
        </div>

        {/* Step 10 Context */}
        <Card variant="default">
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <SparklesIcon className="w-6 h-6 text-accent-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-surface-900 dark:text-surface-100 mb-2">
                  Step 10: Daily Inventory
                </h3>
                <p className="text-surface-700 dark:text-surface-300 leading-relaxed mb-3">
                  "Continued to take personal inventory and when we were wrong promptly admitted it."
                </p>
                <p className="text-sm text-surface-600 dark:text-surface-400 italic">
                  Review your day honestly and identify where you can make amends or improve tomorrow.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What to Expect */}
        <Card variant="filled">
          <CardContent className="space-y-3">
            <h3 className="font-semibold text-surface-900 dark:text-surface-100">
              What to Expect
            </h3>
            <ul className="space-y-2 text-surface-700 dark:text-surface-300">
              <li className="flex items-start gap-2">
                <span className="text-accent-500 mt-1">•</span>
                <span>Answer 4 questions from the Big Book</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent-500 mt-1">•</span>
                <span>Reflect on your morning intentions</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent-500 mt-1">•</span>
                <span>Receive personalized AI insights</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent-500 mt-1">•</span>
                <span>Takes about 5-10 minutes</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Begin Button */}
        <Button
          variant="primary"
          size="lg"
          fullWidth
          onClick={onBegin}
          rightIcon={<MoonIcon className="w-5 h-5" />}
        >
          Begin Nightly Review
        </Button>
      </div>
    </motion.div>
  );
}

export default NightlyWelcome;
